// Minimal shape of the useStorage() interface this helper needs.
interface PageStateStorage {
  get: (key: string) => any;
  set: (key: string, value: any) => void;
}

interface PageStateArgs {
  enabled?: boolean;
  key: string;
  storage: PageStateStorage;
}

// DataTables' own default. Entries older than this are rejected on load.
const STATE_DURATION_SECONDS = 7200;

const storageKeyFor = (key: string) => `datatablePage:${key}`;

/**
 * DataTables options that remember which page a table was on, so leaving the
 * list and coming back returns you to it rather than to page 1.
 *
 * Two things here are deliberate and easy to undo by accident:
 *
 * 1. Only `time` and `start` are ever written. DataTables' state also covers
 *    search, column searches, sort and page length, but those are driven from
 *    the URL and the server-supplied config — persisting them makes the stored
 *    state fight the config and desyncs the filter UI. Narrowing on write
 *    rather than stripping on read means the extra keys cannot leak back in
 *    later; there is nothing to remember to delete.
 *
 * 2. The load callback returns `null`, never `undefined`. DataTables treats an
 *    `undefined` return as "state is loading asynchronously" and waits for a
 *    callback that we never invoke, which leaves the table permanently
 *    uninitialised. A partial `{ time, start }` object is fine — every field
 *    is applied only when present.
 *
 * Keyed on the caller's `key` (the table's JSON source) instead of DataTables'
 * default key, which is built from the table's DOM id and falls back to a
 * global init counter when the table has no id. That counter shifts with
 * initialisation order, and a shifted key silently reads as "no saved page" —
 * the exact symptom this is meant to fix.
 */
export const pageStateOptions = ({ enabled, key, storage }: PageStateArgs) => {
  if (!enabled) return {};

  const storageKey = storageKeyFor(key);

  return {
    stateSave: true,
    stateDuration: STATE_DURATION_SECONDS,
    stateSaveCallback: (
      _settings: unknown,
      data: { time: number; start: number }
    ) => {
      try {
        storage.set(storageKey, { time: data.time, start: data.start });
      } catch {
        // Storage can be unavailable or full (private browsing, quota). Losing
        // the page position is not worth breaking the table over.
      }
    },
    stateLoadCallback: () => {
      try {
        return storage.get(storageKey) ?? null;
      } catch {
        // Stored value isn't parseable — treat it as absent.
        return null;
      }
    }
  };
};
